import React, { JSX, useEffect } from 'react';
import { DocumentResponse, FieldMappings, LanguageData } from '../../types/chat';
import { getPipelineDate } from '../../utils/api';
import { getDocField, getMessageHash, getMessageId, getMessageStatus, processAttachments } from '../../utils/chat';
import { Box, Typography } from '@mui/material';
import { ChatTheme, getItemColour, kikTheme, watermarkContainerSx, watermarkTextSx } from './themes';
import { useAppSelector } from '../../store/hooks';
import MessageContainer from './MessageContainer';
import PageNav from './PageNav';
import PageSummary from './PageSummary';

interface ThreadProps {
	pipelineType?: string;
	viewerRef: React.RefObject<HTMLDivElement | null>;
	caseReference: string;
	chatId: string;
	messageId: string | null;
	hourGap: number;
	chatTheme?: ChatTheme;
	documentResponse: DocumentResponse;
}

const ChatBuilder: React.FC<ThreadProps> = ({
	pipelineType = 'cellebrite-xml',
	viewerRef,
	caseReference,
	chatId,
	messageId,
	hourGap,
	documentResponse,
	chatTheme = kikTheme,
}) => {
	const userTheme = useAppSelector((state) => state.system.theme || 'default');

	const linkStub = `/view/${pipelineType}/${caseReference}/${chatId}`;
	const dedupeFields: string[] = documentResponse.dedupeFields || [];
	const fieldMappings: FieldMappings = documentResponse.fieldMappings;

	let threadItems: Record<number, JSX.Element[]> = {};
	const msgHashes: Set<string> = new Set();
	let currentTime: number = 0;
	let removedDuplicates: number = 0;
	let messagesOnPage: number = 0;

	const pageTitle = `Chat ID ${chatId} (Operation ${caseReference.toLocaleUpperCase()}) (Page ${documentResponse.pageContent.page} / ${documentResponse.totalPages})`;

	for (const doc of documentResponse.pageContent.pageDocs) {
		const msgHash = getMessageHash(doc, dedupeFields);

		if (!msgHashes.has(msgHash)) {
			msgHashes.add(msgHash);
			const msgId = getMessageId(doc, fieldMappings);
			const msgTime = new Date(getDocField(doc, fieldMappings, 'messageTime') as string | number).getTime();

			// stellar chats don't use a "is system message field" but they do leave the sender field blank
			const isSystemMessage =
				fieldMappings.systemMessage === ''
					? getDocField(doc, fieldMappings, 'sender') === ''
					: String(getDocField(doc, fieldMappings, 'systemMessage')) === 'System Message';

			let languageData: LanguageData | undefined;
			if (fieldMappings.languageField && getDocField(doc, fieldMappings, 'languageField')) {
				const guessed = getDocField(doc, fieldMappings, 'languageGuessedLanguages');
				let guessedLanguages: string[] = [];
				if (Array.isArray(guessed)) {
					guessedLanguages = guessed.map((val) => String(val));
				} else if (typeof guessed === 'object') {
					guessedLanguages = [JSON.stringify(guessed, null, 4).replaceAll('\n', '')];
				} else {
					guessedLanguages = [String(guessed)];
				}

				languageData = {
					topLanguage: String(getDocField(doc, fieldMappings, 'languageTopLanguage')),
					reasoning: String(getDocField(doc, fieldMappings, 'languageReasoning')),
					translation: String(getDocField(doc, fieldMappings, 'languageTranslation')),
					guessedLanguages,
					errorLog: String(getDocField(doc, fieldMappings, 'languageError')),
				};
			}

			const msgContainer = (
				<MessageContainer
					linkStub={linkStub}
					chatTheme={chatTheme}
					userTheme={userTheme}
					userIsSender={getDocField(doc, fieldMappings, 'messageDirection') == fieldMappings.fromOwnerDirection ? true : false}
					senderId={String(getDocField(doc, fieldMappings, 'sender'))}
					messageId={msgId}
					messageTime={msgTime}
					messageText={String(getDocField(doc, fieldMappings, 'messageContent')) || '[MESSAGE CONTAINED NO TEXT CONTENT]'}
					attachments={processAttachments(doc, fieldMappings)}
					deleted={getDocField(doc, fieldMappings, 'deletedMessage') !== fieldMappings.notDeletedForm}
					messageStatus={getMessageStatus(getDocField(doc, fieldMappings, 'messageStatus'))}
					systemMessage={isSystemMessage}
					linkedMessage={msgId === messageId}
					languageData={languageData}
				/>
			);

			if (msgTime in threadItems) {
				threadItems[msgTime].push(msgContainer);
			} else {
				threadItems[msgTime] = [msgContainer];
			}
		} else {
			removedDuplicates++;
		}
	}

	threadItems = Object.fromEntries(Object.entries(threadItems).sort(([keyA], [keyB]) => Number(keyA) - Number(keyB)));
	messagesOnPage = msgHashes.size;

	useEffect(() => {
		const linkedElement = document.getElementById(`message-${messageId}`);
		if (linkedElement) {
			linkedElement.scrollIntoView();
		}
	}, []);

	return (
		<Box maxHeight={'100%'} ref={viewerRef} sx={{ overflowY: 'scroll' }}>
			{/* initDoc */}
			<Box padding={2} width={'100%'} sx={{ backgroundColor: 'background.paper' }} data-testid={'chat-viewer-title'}>
				<Typography component={'h1'} variant="h5" textAlign={'center'} fontWeight={'bold'}>
					{pageTitle}
				</Typography>
			</Box>

			<PageSummary {...documentResponse.summaryInfo} messagesOnPage={messagesOnPage} removedDuplicates={removedDuplicates} />

			{/* buildDoc */}
			{Object.keys(threadItems)
				.map(Number)
				.map((threadTimestamp) => {
					const timeDeltaMS = new Date(threadTimestamp).getTime() - new Date(currentTime).getTime();
					currentTime = threadTimestamp;
					const addTimeBox = timeDeltaMS / (1000 * 60 * 60) > hourGap;

					return (
						<Box
							width={'50%'}
							maxWidth={'50%'}
							justifySelf={'center'}
							key={`thread-${threadTimestamp}`}
							sx={{
								'@media print': {
									width: '80%',
									maxWidth: '80%',
								},
							}}
						>
							{addTimeBox && (
								<Box
									margin={'auto'}
									width={'15%'}
									textAlign={'center'}
									borderRadius={2}
									marginY={2}
									sx={{ backgroundColor: getItemColour('timeBar', chatTheme, userTheme) }}
									data-testid={`thread-timebox-${threadTimestamp}`}
								>
									<Typography component="p" variant="subtitle1">
										{getPipelineDate(threadTimestamp)}
									</Typography>
								</Box>
							)}

							{threadItems[threadTimestamp].map((item, i) => (
								<React.Fragment key={`message-${messageId}-${i}`}>{item}</React.Fragment>
							))}
						</Box>
					);
				})}

			<Box sx={watermarkContainerSx}>
				<Typography sx={watermarkTextSx}>NOT FOR{'\n'}EVIDENTIAL USE</Typography>
			</Box>

			<PageNav page={documentResponse.pageContent.page} totalPages={documentResponse.totalPages} containerRef={viewerRef} />
		</Box>
	);
};

export default ChatBuilder;
